import { ImpactStyle } from '@capacitor/haptics';
import { IonPage, useIonViewWillEnter } from '@ionic/react';
import {
  FilterItem,
  SupportedCurrency,
  LocaleCurrencyMapper,
  useDeepUpdateEffect,
  getFiltersValues,
  useDeepCompareEffect,
  getFiltersQuery,
  isFilterSet,
} from '@mindly/ui-components';
import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TranslatedFiltersWidget } from 'src/translated-components';
import { useDispatch, useSelector } from 'react-redux';
import { setIsActive } from '../../redux/tabBar/reducer';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { userSelector } from 'src/redux/user/selector';
import { setFiltersValues, setFiltersItems, setTotalItems } from 'src/redux/specialists/reducer';
import { useSwipeBackGesture } from 'src/shared/hooks/useSwipeBackGesture';
import debounce from 'lodash/debounce';
import {
  specialistsFiltersItemsSelector,
  specialistsFiltersLoadingSelector,
  specialistsFiltersValuesSelector,
  specialistsTotalItemsSelector,
} from 'src/redux/specialists/selector';
import { PublicRoutes } from 'src/routes/navigation';
import { getSpecialistTotal } from 'src/api/specialist/specialist';
import { fetchSpecialistsFilters } from 'src/redux/specialists/thunk';
import { saveFilters } from 'src/shared/utils/filters';
import { useAnalytics } from 'src/shared';
import { useFilters } from 'src/shared/hooks/useFilters';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const SpicialistListFilters: React.FC = () => {
  const pageRef = useRef<Node>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('ui');
  const currentUser = useSelector(userSelector);
  const filtersFromStore = useSelector(specialistsFiltersItemsSelector);
  const totalItems = useSelector(specialistsTotalItemsSelector);
  const [filters, setFilters] = useState<FilterItem[]>(filtersFromStore);
  const [isLoadingTotalItems, setIsLoadingTotalItems] = useState<boolean>(false);
  const loadingState = useSelector(specialistsFiltersLoadingSelector);
  const isLoading = ['pending', 'idle'].includes(loadingState);
  const originalFiltersValues = useSelector(specialistsFiltersValuesSelector);
  const { trackPredefinedEvent } = useAnalytics();
  const { getFiltersDataForAnalytics } = useFilters();
  const isLocalizedAreasOfWorkByClientProblem = useFeatureIsOn('localized_areas_of_work_by_client_problem');

  useSwipeBackGesture(pageRef);

  useDeepUpdateEffect(() => {
    setFilters(filtersFromStore);
  }, [filtersFromStore]);

  const applyFiltersValues = useCallback(
    async (newFilters: FilterItem[]) => {
      const filtersValues = getFiltersValues(newFilters);
      const hasFilters = isFilterSet(filtersValues);
      if (hasFilters) {
        const filtersQuery = getFiltersQuery(filtersValues);
        setIsLoadingTotalItems(true);
        try {
          const total = await getSpecialistTotal(filtersQuery);
          setIsLoadingTotalItems(false);
          dispatch(setTotalItems(total));
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoadingTotalItems(false);
        }
      } else {
        dispatch(setTotalItems(undefined));
      }
    },
    [originalFiltersValues]
  );

  const debouncedSetFiltersValues = useCallback(
    debounce(async (f) => {
      if (!f.length) return;
      await applyFiltersValues(f);
    }, 1000),
    [applyFiltersValues]
  );

  useDeepCompareEffect(() => {
    debouncedSetFiltersValues(filters);
  }, [filters, debouncedSetFiltersValues]);

  const handleClickBackButton = useCallback(async () => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    history.back();
  }, [history]);

  useIonViewWillEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    dispatch(setIsActive(false));
  }, [currentUser?.id]);

  useDeepCompareEffect(() => {
    if (loadingState !== 'idle') return;
    dispatch(fetchSpecialistsFilters({ t, isLocalizedAreasOfWorkByClientProblem }));
  }, [t, loadingState]);

  const handleChangeFilter = useCallback((newFilters: FilterItem[]) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback(
    (action: 'apply' | 'clear', newFilters: FilterItem[]) => {
      if (action === 'apply') {
        const filtersValues = getFiltersValues(newFilters);
        dispatch(setFiltersValues(filtersValues));
        dispatch(setFiltersItems(newFilters));
        saveFilters(filtersValues, currentUser?.app_lang, currentUser?.id ?? '');
        const filtersDataForAnalytics = getFiltersDataForAnalytics(filtersValues);
        trackPredefinedEvent('SPECIALIST_LIST_FILTERS_SUBMITTED', filtersDataForAnalytics);
        history.back();
      }
      setFilters(newFilters);
    },
    [currentUser?.app_lang, currentUser?.id]
  );

  return (
    <IonPage ref={pageRef}>
      <TranslatedFiltersWidget
        isLoading={isLoading}
        totalItems={totalItems}
        isLoadingTotalItems={isLoadingTotalItems}
        initialFilters={filters}
        currency={
          LocaleCurrencyMapper[currentUser?.app_lang as keyof typeof LocaleCurrencyMapper] ?? SupportedCurrency.UAH
        }
        onClose={handleClickBackButton}
        onChange={handleChangeFilter}
        onApply={handleApplyFilters}
        staticFiltersKeys={currentUser?.possible_price_filters ? ['session_price'] : undefined}
      />
    </IonPage>
  );
};

export default SpicialistListFilters;
